import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Users,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';

interface LinearTeam {
  id: string;
  key: string;
  name: string;
  description?: string;
}

interface LinearImportModalProps {
  onClose: () => void;
}

const LinearImportModal: React.FC<LinearImportModalProps> = ({ onClose }) => {
  const [selectedTeams, setSelectedTeams] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'teams' | 'all'>('teams');
  const queryClient = useQueryClient();

  // Fetch Linear teams
  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['linear-teams'],
    queryFn: async () => {
      const response = await (apiClient as any).getLinearTeams();
      return response.data ?? response;
    },
    retry: 1,
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (data: { team_ids?: string[]; import_all?: boolean }) => {
      const response = await (apiClient as any).importLinear(data);
      return response.data ?? response;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      const message = data?.total_issues !== undefined
        ? `Successfully imported ${data.total_issues} issues from ${data.total_teams} team(s)!`
        : 'Linear data imported successfully!';
      alert(message);
      onClose();
    },
    onError: (error: any) => {
      alert(`Failed to import Linear data: ${error.message}`);
    },
  });

  const teams: LinearTeam[] = Array.isArray(teamsData) ? teamsData : [];

  const handleTeamToggle = (teamId: string) => {
    const next = new Set(selectedTeams);
    if (next.has(teamId)) {
      next.delete(teamId);
    } else {
      next.add(teamId);
    }
    setSelectedTeams(next);
  };

  const handleSelectAll = () => {
    if (selectedTeams.size === teams.length) {
      setSelectedTeams(new Set());
    } else {
      setSelectedTeams(new Set(teams.map((t) => t.id)));
    }
  };

  const handleImport = () => {
    if (activeTab === 'all') {
      importMutation.mutate({ import_all: true });
    } else if (selectedTeams.size > 0) {
      importMutation.mutate({ team_ids: Array.from(selectedTeams) });
    } else {
      alert('Please select at least one team to import');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#5E6AD2] to-[#3F4AB5] rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Linear Data</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select teams to import issues from
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('teams')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'teams'
                ? 'border-[#5E6AD2] text-[#5E6AD2]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>By Team ({teams.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-[#5E6AD2] text-[#5E6AD2]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>Import All Teams</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'teams' ? (
            <div>
              <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedTeams.size === teams.length && teams.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-[#5E6AD2] rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Teams
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedTeams.size} of {teams.length} selected
                </span>
              </div>

              {teamsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 text-[#5E6AD2] animate-spin" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading teams...</p>
                  </div>
                </div>
              ) : teams.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    No Teams Found
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No teams available in your Linear workspace
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teams.map((team) => (
                    <label
                      key={team.id}
                      className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTeams.has(team.id)}
                        onChange={() => handleTeamToggle(team.id)}
                        className="w-4 h-4 text-[#5E6AD2] rounded border-gray-300 dark:border-gray-600 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{team.name}</h4>
                          {team.key && (
                            <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full font-mono">
                              {team.key}
                            </span>
                          )}
                        </div>
                        {team.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{team.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8">
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#5E6AD2] to-[#3F4AB5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Import All Linear Teams
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This will import all issues (with comments) from all accessible teams.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Users className="h-6 w-6 text-[#5E6AD2] mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">All Teams</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{teams.length} available</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Issues</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Including comments</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Date Range</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Full history</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Auto Sync</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Manual re-import</p>
                  </div>
                </div>

                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                  <p className="text-sm text-indigo-800 dark:text-indigo-200">
                    Tip: After import, team names and label names become candidate customer mappings on the Map Organizations screen.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'teams' ? (
              <span>
                {selectedTeams.size > 0
                  ? `${selectedTeams.size} team${selectedTeams.size > 1 ? 's' : ''} selected`
                  : 'Select teams to import their issues'}
              </span>
            ) : (
              <span>Complete sync of all teams</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || (activeTab === 'teams' && selectedTeams.size === 0)}
              className="bg-gradient-to-r from-[#5E6AD2] to-[#3F4AB5] hover:from-[#3F4AB5] hover:to-[#2C3893] text-white"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {activeTab === 'all' ? 'Import All Teams' : `Import ${selectedTeams.size} Team${selectedTeams.size !== 1 ? 's' : ''}`}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinearImportModal;
